import React, { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "./api";
import { isAuthenticated, logout } from "./auth";
import LoginScreen from "./Login";
import Weather from "./Weather";
import SettingsModal from "./Settings";
import "./App.css";

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUSES = [
  { value: "planned",     label: "Planned",     color: "#6B7280" },
  { value: "in_progress", label: "In Progress", color: "#F59E0B" },
  { value: "complete",    label: "Complete",    color: "#10B981" },
  { value: "canceled",    label: "Canceled",    color: "#EF4444" },
];

const PRIORITIES = [
  { value: "low",    label: "Low",    color: "#10B981" },
  { value: "medium", label: "Medium", color: "#F59E0B" },
  { value: "high",   label: "High",   color: "#EF4444" },
];

const CATEGORIES = [
  // Home
  "Roof", "Gutters", "Plumbing", "HVAC", "Electrical",
  "Foundation", "Exterior", "Interior", "Landscaping", "General",
  // Vehicles
  "VW Jetta", "Mazda 6", "Honda Rebel",
  // Other
  "Bikes",
];

const RECURRENCES = [
  { value: "none",      label: "One-time" },
  { value: "weekly",    label: "Weekly" },
  { value: "biweekly",  label: "Every 2 weeks" },
  { value: "monthly",   label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "biannual",  label: "Every 6 months" },
  { value: "annual",    label: "Annual" },
];

// Domain → category membership for grouping the task list
const DOMAIN_CATEGORIES = {
  "🏠 Home":     ["Roof", "Gutters", "Plumbing", "HVAC", "Electrical", "Foundation",
                  "Exterior", "Interior", "Landscaping", "General", "Pest Control"],
  "🚗 Vehicles": ["VW Jetta", "Mazda 6", "Honda Rebel"],
  "🚲 Bikes":    ["Bikes"],
};

// Tasks due more than this many days out are tucked into the "Upcoming" section
const UPCOMING_DAYS = 30;

// A task is "future" if it hasn't been started and isn't due within UPCOMING_DAYS
function isFutureTask(task) {
  if (task.status !== "planned" || !task.due_date) return false;
  const due = new Date(task.due_date + "T00:00:00");
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() + UPCOMING_DAYS);
  return due > cutoff;
}

const EMPTY_FORM = {
  title: "", category: "General", status: "planned",
  priority: "medium", due_date: "", notes: "", recurrence: "none",
};

// ── Sub-components ─────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const s = STATUSES.find(x => x.value === status) || STATUSES[0];
  return (
    <span className="badge" style={{ background: s.color + "22", color: s.color, border: `1px solid ${s.color}44` }}>
      {s.label}
    </span>
  );
}

function PriorityDot({ priority }) {
  const p = PRIORITIES.find(x => x.value === priority) || PRIORITIES[1];
  return <span className="priority-dot" style={{ background: p.color }} title={p.label} />;
}

function TaskModal({ task, categoryOptions, onClose, onSave }) {
  const [form, setForm] = useState(task || EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // When true, show a free-text input for naming a brand-new category
  const [addingCategory, setAddingCategory] = useState(false);

  const isEdit = !!task?.task_id;

  function set(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) { setError("Title is required"); return; }
    setSaving(true);
    setError(null);
    // Normalize a blank/whitespace category back to the default
    const payload = { ...form, category: form.category.trim() || "General" };
    try {
      if (isEdit) {
        const updated = await api.updateTask(task.task_id, payload);
        onSave(updated.task);
      } else {
        const created = await api.createTask(payload);
        onSave(created.task);
      }
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2>{isEdit ? "Edit Task" : "New Task"}</h2>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && <div className="error-banner">{error}</div>}

          <label>
            Title *
            <input
              type="text"
              value={form.title}
              onChange={e => set("title", e.target.value)}
              placeholder="e.g. Clean gutters before winter"
              autoFocus
            />
          </label>

          <div className="form-row">
            <label>
              Category
              {addingCategory ? (
                <>
                  <input
                    type="text"
                    value={form.category}
                    onChange={e => set("category", e.target.value)}
                    placeholder="New category name"
                    autoFocus
                  />
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => { setAddingCategory(false); set("category", categoryOptions[0] || "General"); }}
                  >
                    ↩ Pick an existing category
                  </button>
                </>
              ) : (
                <select
                  value={form.category}
                  onChange={e => {
                    if (e.target.value === "__new__") {
                      setAddingCategory(true);
                      set("category", "");
                    } else {
                      set("category", e.target.value);
                    }
                  }}
                >
                  {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
                  <option value="__new__">➕ Add new category…</option>
                </select>
              )}
            </label>

            <label>
              Priority
              <select value={form.priority} onChange={e => set("priority", e.target.value)}>
                {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </label>
          </div>

          <div className="form-row">
            <label>
              Status
              <select value={form.status} onChange={e => set("status", e.target.value)}>
                {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </label>

            <label>
              Due Date
              <input
                type="date"
                value={form.due_date}
                onChange={e => set("due_date", e.target.value)}
              />
            </label>
          </div>

          <label>
            Recurrence
            <select value={form.recurrence || "none"} onChange={e => set("recurrence", e.target.value)}>
              {RECURRENCES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </label>

          <label>
            Notes
            <textarea
              value={form.notes}
              onChange={e => set("notes", e.target.value)}
              placeholder="Any additional details, contractor info, cost estimates…"
              rows={4}
            />
          </label>

          {isEdit && task?.history?.length > 0 && (
            <div className="history-section">
              <div className="history-header">Completion History</div>
              <div className="history-list">
                {task.history.map((entry, i) => (
                  <div key={i} className="history-entry">
                    <span className="history-check">✓</span>
                    <span className="history-date">
                      {new Date(entry.completed_at).toLocaleDateString("en-US", {
                        month: "short", day: "numeric", year: "numeric",
                      })}
                    </span>
                    {entry.due_date && (
                      <span className="history-was-due">was due {entry.due_date}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Task"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TaskCard({ task, onEdit, onDelete, onStatusChange, confirmDialog, showError }) {
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    const ok = await confirmDialog(`Delete "${task.title}"?`);
    if (!ok) return;
    setDeleting(true);
    try {
      await api.deleteTask(task.task_id);
      onDelete(task.task_id);
    } catch (err) {
      showError(err.message);
      setDeleting(false);
    }
  }

  async function handleStatusChange(newStatus) {
    try {
      const updated = await api.updateTask(task.task_id, { status: newStatus });
      onStatusChange(updated.task);
    } catch (err) {
      showError(err.message);
    }
  }

  const isOverdue = task.due_date && task.status !== "complete" && task.status !== "canceled"
    && new Date(task.due_date) < new Date();

  return (
    <div className={`task-card ${task.status} ${isOverdue ? "overdue" : ""}`}>
      <div className="task-card-header">
        <div className="task-title-row">
          <PriorityDot priority={task.priority} />
          <h3 className="task-title">{task.title}</h3>
        </div>
        <div className="task-card-actions">
          <button className="btn-icon" onClick={() => onEdit(task)} title="Edit">✏️</button>
          <button className="btn-icon danger" onClick={handleDelete} disabled={deleting} title="Delete">🗑</button>
        </div>
      </div>

      <div className="task-meta">
        <span className="category-tag">{task.category}</span>
        <StatusBadge status={task.status} />
        {task.recurrence && task.recurrence !== "none" && (
          <span className="recurrence-tag" title="Recurring task">
            🔁 {RECURRENCES.find(r => r.value === task.recurrence)?.label || task.recurrence}
          </span>
        )}
        {task.due_date && (
          <span className={`due-date ${isOverdue ? "overdue-text" : ""}`}>
            📅 {new Date(task.due_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            {isOverdue && " ⚠️"}
          </span>
        )}
      </div>

      {task.notes && <p className="task-notes">{task.notes}</p>}

      <div className="quick-status">
        {STATUSES.filter(s => s.value !== task.status).map(s => (
          <button
            key={s.value}
            className="btn-status"
            style={{ color: s.color }}
            onClick={() => handleStatusChange(s.value)}
          >
            → {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Error Boundary ────────────────────────────────────────────────────────────

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("App error:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <div className="error-boundary-icon">⚠️</div>
            <h2>Something went wrong</h2>
            <p className="error-boundary-msg">{this.state.error?.message || "An unexpected error occurred."}</p>
            <button
              className="btn btn-primary"
              onClick={() => this.setState({ hasError: false, error: null })}
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Confirm Dialog / Toast ────────────────────────────────────────────────────
// Replaces window.confirm()/alert() — native browser dialogs that can't be
// styled, block the main thread, and look out of place next to the app's
// own modal system (TaskModal, SettingsModal). Reuses the same modal CSS.

function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onCancel()}>
      <div className="modal modal-confirm">
        <div className="modal-header">
          <h2>Confirm</h2>
        </div>
        <div className="modal-form">
          <p>{message}</p>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onCancel} autoFocus>Cancel</button>
            <button type="button" className="btn btn-danger" onClick={onConfirm}>Delete</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Toast({ message, onDismiss }) {
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(onDismiss, 6000);
    return () => clearTimeout(timer);
  }, [message, onDismiss]);

  if (!message) return null;

  return (
    <div className="toast" role="alert">
      <span className="toast-message">⚠️ {message}</span>
      <button className="toast-close" onClick={onDismiss} aria-label="Dismiss">✕</button>
    </div>
  );
}

// ── Domain Section ────────────────────────────────────────────────────────────

function DomainSection({ domain, tasks, onEdit, onDelete, onStatusChange, confirmDialog, showError }) {
  const [collapsed, setCollapsed] = useState(false);

  if (tasks.length === 0) return null;

  return (
    <div className="domain-section">
      <button className="domain-header" onClick={() => setCollapsed(c => !c)}>
        <span className="domain-title">{domain}</span>
        <span className="domain-count">{tasks.length}</span>
        <span className="domain-chevron">{collapsed ? "▶" : "▼"}</span>
      </button>
      {!collapsed && (
        <div className="task-grid">
          {tasks.map(task => (
            <TaskCard
              key={task.task_id}
              task={task}
              onEdit={onEdit}
              onDelete={onDelete}
              onStatusChange={onStatusChange}
              confirmDialog={confirmDialog}
              showError={showError}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Upcoming Section ──────────────────────────────────────────────────────────

function UpcomingSection({ tasks, onEdit, onDelete, onStatusChange, confirmDialog, showError }) {
  const [collapsed, setCollapsed] = useState(true);

  if (tasks.length === 0) return null;

  const sorted = [...tasks].sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));

  return (
    <div className="domain-section upcoming-section">
      <button className="domain-header" onClick={() => setCollapsed(c => !c)}>
        <span className="domain-title">📅 Upcoming (due in {UPCOMING_DAYS}+ days)</span>
        <span className="domain-count">{sorted.length}</span>
        <span className="domain-chevron">{collapsed ? "▶" : "▼"}</span>
      </button>
      {!collapsed && (
        <div className="task-grid">
          {sorted.map(task => (
            <TaskCard
              key={task.task_id}
              task={task}
              onEdit={onEdit}
              onDelete={onDelete}
              onStatusChange={onStatusChange}
              confirmDialog={confirmDialog}
              showError={showError}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────

export default function App() {
  const [authed, setAuthed] = useState(isAuthenticated());

  if (!authed) {
    return (
      <ErrorBoundary>
        <LoginScreen onAuthenticated={() => setAuthed(true)} />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <MainApp onLogout={() => { logout(); setAuthed(false); }} />
    </ErrorBoundary>
  );
}

function MainApp({ onLogout }) {
  const [tasks, setTasks]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [statusFilter, setFilter]     = useState("all");
  const [categoryFilter, setCat]      = useState("all");
  const [searchQuery, setSearch]      = useState("");
  const [modalTask, setModalTask]     = useState(null);
  const [modalOpen, setModalOpen]     = useState(false);
  const [settings, setSettings]       = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [confirmState, setConfirmState] = useState(null); // { message, resolve } | null
  const [toastMessage, setToastMessage] = useState(null);

  // Shows the shared confirm dialog and resolves once the user picks an
  // option — lets any component `await confirmDialog("...")` instead of
  // blocking on window.confirm().
  const confirmDialog = useCallback((message) => {
    return new Promise(resolve => setConfirmState({ message, resolve }));
  }, []);

  function resolveConfirm(result) {
    confirmState?.resolve(result);
    setConfirmState(null);
  }

  const showError = useCallback((message) => setToastMessage(message), []);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.listTasks(statusFilter === "all" ? null : statusFilter);
      setTasks(data.tasks);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  useEffect(() => {
    api.getSettings()
      .then(data => setSettings(data.settings))
      .catch(() => setSettings({ address: "", lat: 33.749, lon: -84.388, photo_url: null }));
  }, []);

  function openNew()      { setModalTask(null); setModalOpen(true); }
  function openEdit(task) { setModalTask(task); setModalOpen(true); }
  function closeModal()   { setModalOpen(false); setModalTask(null); }

  function handleSave(savedTask) {
    setTasks(prev => {
      const exists = prev.find(t => t.task_id === savedTask.task_id);
      return exists
        ? prev.map(t => t.task_id === savedTask.task_id ? savedTask : t)
        : [savedTask, ...prev];
    });
  }

  function handleDelete(id) {
    setTasks(prev => prev.filter(t => t.task_id !== id));
  }

  function handleStatusChange(updatedTask) {
    setTasks(prev => prev.map(t => t.task_id === updatedTask.task_id ? updatedTask : t));
  }

  // Dynamic category list derived from loaded tasks (preserves canonical order)
  const availableCategories = useMemo(() => {
    const inTasks = new Set(tasks.map(t => t.category).filter(Boolean));
    const canonical = CATEGORIES.filter(c => inTasks.has(c));
    const extra = [...inTasks].filter(c => !CATEGORIES.includes(c)).sort();
    return [...canonical, ...extra];
  }, [tasks]);

  // Full suggestion list for the task modal: all canonical categories plus any
  // custom ones already used by existing tasks. Users can also type a brand-new one.
  const categoryOptions = useMemo(() => {
    const inTasks = new Set(tasks.map(t => t.category).filter(Boolean));
    const extra = [...inTasks].filter(c => !CATEGORIES.includes(c)).sort();
    return [...CATEGORIES, ...extra];
  }, [tasks]);

  // Client-side filtering (category + search on top of server-side status filter)
  const filtered = tasks.filter(t => {
    if (categoryFilter !== "all" && t.category !== categoryFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return t.title.toLowerCase().includes(q) || t.notes?.toLowerCase().includes(q);
    }
    return true;
  });

  // Group filtered tasks into domains for the grouped view
  function groupByDomain(taskList) {
    const assigned = new Set();
    const groups = [];
    for (const [domain, cats] of Object.entries(DOMAIN_CATEGORIES)) {
      const domainTasks = taskList.filter(t => cats.includes(t.category));
      domainTasks.forEach(t => assigned.add(t.task_id));
      groups.push([domain, domainTasks]);
    }
    const other = taskList.filter(t => !assigned.has(t.task_id));
    if (other.length > 0) groups.push(["📦 Other", other]);
    return groups;
  }

  // Split into actionable (late or due soon) vs future tasks
  const currentTasks  = filtered.filter(t => !isFutureTask(t));
  const upcomingTasks = filtered.filter(isFutureTask);

  const domainGroups = groupByDomain(currentTasks);

  // Stats for header summary
  const counts = STATUSES.reduce((acc, s) => {
    acc[s.value] = tasks.filter(t => t.status === s.value).length;
    return acc;
  }, {});

  return (
    <div className="app">
      <header className="app-header">

        {/* ── Property dashboard ── */}
        <div className="property-dashboard">
          {settings?.photo_url && (
            <img src={settings.photo_url} alt="House" className="house-photo" />
          )}
          <div className="property-info">
            <div className="header-content">
              <div className="header-title">
                <span className="header-icon">🏠</span>
                <div>
                  <h1>Cocoon</h1>
                  {settings?.address
                    ? <p className="property-address">{settings.address}</p>
                    : <p className="header-sub">Track repairs, maintenance & projects</p>
                  }
                </div>
              </div>
              <div className="header-actions">
                <button className="btn btn-ghost btn-settings" onClick={() => setSettingsOpen(true)} title="Settings">⚙️</button>
                <button className="btn btn-primary" onClick={openNew}>+ New Task</button>
                <button className="btn btn-ghost btn-logout" onClick={onLogout} title="Sign out">⏏</button>
              </div>
            </div>
            {settings && (
              <Weather lat={settings.lat} lon={settings.lon} />
            )}
          </div>
        </div>

        <div className="stats-row">
          {STATUSES.map(s => (
            <div key={s.value} className="stat-chip" style={{ borderColor: s.color + "44" }}>
              <span className="stat-num" style={{ color: s.color }}>{counts[s.value] || 0}</span>
              <span className="stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </header>

      <div className="toolbar">
        <input
          type="text"
          className="search-input"
          placeholder="🔍 Search tasks…"
          value={searchQuery}
          onChange={e => setSearch(e.target.value)}
        />

        <div className="filter-group">
          <label>Status</label>
          <select value={statusFilter} onChange={e => setFilter(e.target.value)}>
            <option value="all">All</option>
            {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>

        <div className="filter-group">
          <label>Category</label>
          <select value={categoryFilter} onChange={e => setCat(e.target.value)}>
            <option value="all">All</option>
            {availableCategories.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <main className="task-main">
        {loading && <div className="state-msg">Loading tasks…</div>}
        {error   && <div className="state-msg error">⚠️ {error} <button onClick={loadTasks}>Retry</button></div>}
        {!loading && !error && filtered.length === 0 && (
          <div className="state-msg empty">
            <span>🔧</span>
            <p>{tasks.length === 0 ? "No tasks yet. Add your first maintenance task!" : "No tasks match your filters."}</p>
            {tasks.length === 0 && <button className="btn btn-primary" onClick={openNew}>Add First Task</button>}
          </div>
        )}
        {!loading && !error && filtered.length > 0 && currentTasks.length === 0 && (
          <div className="state-msg empty">
            <span>✅</span>
            <p>Nothing late or due in the next {UPCOMING_DAYS} days. All caught up!</p>
          </div>
        )}
        {!loading && !error && domainGroups.map(([domain, domainTasks]) => (
          <DomainSection
            key={domain}
            domain={domain}
            tasks={domainTasks}
            onEdit={openEdit}
            onDelete={handleDelete}
            onStatusChange={handleStatusChange}
            confirmDialog={confirmDialog}
            showError={showError}
          />
        ))}
        {!loading && !error && (
          <UpcomingSection
            tasks={upcomingTasks}
            onEdit={openEdit}
            onDelete={handleDelete}
            onStatusChange={handleStatusChange}
            confirmDialog={confirmDialog}
            showError={showError}
          />
        )}
      </main>

      {modalOpen && (
        <TaskModal
          task={modalTask}
          categoryOptions={categoryOptions}
          onClose={closeModal}
          onSave={handleSave}
        />
      )}

      {settingsOpen && (
        <SettingsModal
          settings={settings || { address: "", lat: 33.749, lon: -84.388, photo_url: null }}
          onClose={() => setSettingsOpen(false)}
          onSave={s => setSettings(s)}
        />
      )}

      {confirmState && (
        <ConfirmDialog
          message={confirmState.message}
          onConfirm={() => resolveConfirm(true)}
          onCancel={() => resolveConfirm(false)}
        />
      )}

      <Toast message={toastMessage} onDismiss={() => setToastMessage(null)} />
    </div>
  );
}
